import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Box, CircularProgress, IconButton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { navigate } from '../../redux/navigator/action';
import { APP_ROUTES } from '../../types/global/routes.types';
import {
    ensureObsidianDashboardFonts,
    OBSIDIAN,
    OBSIDIAN_BODY_FONT,
    OBSIDIAN_DISPLAY_FONT,
    dashboardShellSx,
} from '../../constants/obsidianDashboardTheme';
import { SandboxHlsPlayer } from '../../components/Sandbox/SandboxHlsPlayer';
import { SandboxClassPlaylist, type PlaylistLesson } from '../../components/Sandbox/SandboxClassPlaylist';
import { resolveSandboxMediaUrl } from '../../utils/sandboxCourseApi';
import { fetchLessonProject, fetchSandboxCourse, saveSandboxProgress } from '../../redux/sandbox/actions';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import type { IProject } from '../../types/global/helper';
import { SandboxAssignmentSubmitPanel } from '../../components/Sandbox/SandboxAssignmentSubmitPanel';
import { SandboxLessonDiscussion } from '../../components/Sandbox/SandboxLessonDiscussion';
import { SandboxRichText } from '../../components/Sandbox/SandboxRichText';
import { SandboxClassAssetsPanel } from '../../components/Sandbox/SandboxClassAssetsPanel';
import type { RootState } from '../../redux/root-reducer';
import { getJWtDetails } from '../../utils/AppUtils';
import type { SandboxClassAsset } from '../../utils/sandboxCourseApi';

const PLAYLIST_WIDTH = 300;
/** Mock cinema shell — deeper than app shell `#1c1c1d`. */
const CINEMA = '#0e0e0e';

type TabKey = 'overview' | 'comments' | 'project' | 'assets' | 'classes';

export function SandboxPlayerPage() {
    const { courseId = '', lessonId = '' } = useParams<{ courseId: string; lessonId: string }>();
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [tab, setTab] = useState<TabKey>('overview');
    const [loading, setLoading] = useState(true);
    const [project, setProject] = useState<IProject | null>(null);
    const [lesson, setLesson] = useState<any>(null);
    const [courseTitle, setCourseTitle] = useState('');
    const [courseDescription, setCourseDescription] = useState('');
    const [masteryTier, setMasteryTier] = useState('');
    const [teacherName, setTeacherName] = useState('');
    const [teacherPhoto, setTeacherPhoto] = useState<string | null>(null);
    const [lessons, setLessons] = useState<PlaylistLesson[]>([]);
    const [canWatchAll, setCanWatchAll] = useState(false);
    const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
    const [startSeconds, setStartSeconds] = useState(0);
    const [playlistOpen, setPlaylistOpen] = useState(true);
    const [classAssets, setClassAssets] = useState<SandboxClassAsset[]>([]);
    const [classAssetCount, setClassAssetCount] = useState(0);
    const lessonCompletedRef = useRef(false);
    const { user } = getJWtDetails();
    const isSignedIn = Boolean(user?._id);

    useEffect(() => {
        ensureObsidianDashboardFonts();
    }, []);

    useEffect(() => {
        setPlaylistOpen(isDesktop);
    }, [isDesktop]);

    useEffect(() => {
        setTab('overview');
        lessonCompletedRef.current = false;
    }, [lessonId]);

    const load = useCallback(async () => {
        if (!courseId || !lessonId) return;
        setLoading(true);
        try {
            const [lessonRes, courseRes]: any[] = await Promise.all([
                dispatchPromise(fetchLessonProject({ courseId, lessonId })),
                dispatchPromise(fetchSandboxCourse(courseId)),
            ]);
            const lessonData = lessonRes?.data || lessonRes;
            const coursePayload = courseRes?.data || courseRes;
            const course = coursePayload?.course;
            setProject(lessonData?.project || null);
            setLesson(lessonData?.lesson || null);
            setCourseTitle(course?.title || '');
            setCourseDescription(course?.description || '');
            setMasteryTier(String(course?.mastery_tier || '').replace(/_/g, ' '));
            setCanWatchAll(Boolean(coursePayload?.enrolled || coursePayload?.is_owner));
            setClassAssets(Array.isArray(course?.class_assets) ? course.class_assets : []);
            setClassAssetCount(
                Array.isArray(course?.class_assets) && course.class_assets.length
                    ? course.class_assets.length
                    : Number(course?.class_asset_count) || 0,
            );

            const teacher = typeof course?.teacher === 'object' ? course.teacher : null;
            setTeacherName(
                teacher
                    ? [teacher.firstName, teacher.lastName].filter(Boolean).join(' ') || teacher.userName || 'Mentor'
                    : 'Mentor',
            );
            setTeacherPhoto(resolveSandboxMediaUrl(teacher?.photo) || null);

            const flat: PlaylistLesson[] = [];
            (course?.modules || []).forEach((mod: any) => {
                (mod.lessons || []).forEach((l: any) => {
                    flat.push({
                        _id: l._id,
                        title: l.title,
                        is_free_preview: Boolean(l.is_free_preview),
                        has_assignment: Boolean(l.has_assignment),
                        project: l.project || null,
                        has_project: Boolean(l.has_project || l.project),
                        moduleTitle: mod.title,
                        description: l.description || '',
                    });
                });
            });
            setLessons(flat);

            const progressLessons = coursePayload?.progress?.lessons || [];
            setCompletedIds(new Set(progressLessons.filter((p: any) => p.completed).map((p: any) => String(p.lesson))));
            const saved = progressLessons.find((p: any) => String(p.lesson) === String(lessonId));
            lessonCompletedRef.current = Boolean(saved?.completed);
            const savedPos = Number(saved?.position_seconds) || 0;
            const savedDur = Number(saved?.duration_seconds) || 0;
            // Rewatch completed lessons from the start; otherwise resume mid-watch.
            if (saved?.completed) {
                setStartSeconds(0);
            } else if (savedDur > 0 && savedPos / savedDur >= 0.95) {
                setStartSeconds(0);
            } else {
                setStartSeconds(savedPos);
            }
        } catch (e: any) {
            showToastMessage(e?.message || 'Unable to load lesson', 'error');
        } finally {
            setLoading(false);
        }
    }, [courseId, lessonId, dispatchPromise]);

    useEffect(() => {
        load();
    }, [load]);

    const persist = useCallback(
        async (position: number, duration: number, completed?: boolean) => {
            if (!courseId || !lessonId) return;
            const dur = Math.max(0, duration || 0);
            const pos = Math.max(0, position || 0);
            const nearEnd = dur > 0 && pos / dur >= 0.9;
            const markDone = Boolean(completed) || nearEnd || lessonCompletedRef.current;
            if (markDone) lessonCompletedRef.current = true;
            try {
                await dispatchPromise(
                    saveSandboxProgress({
                        courseId,
                        lessonId,
                        body: {
                            position_seconds: Math.floor(pos),
                            duration_seconds: Math.floor(dur),
                            ...(markDone ? { completed: true } : {}),
                        },
                    }),
                );
                if (markDone) {
                    setCompletedIds(prev => new Set(prev).add(String(lessonId)));
                }
            } catch {
                /* soft-fail progress */
            }
        },
        [courseId, lessonId, dispatchPromise],
    );

    const handleLessonCompleted = useCallback(
        (position: number, duration: number) => {
            void persist(position, duration, true);
        },
        [persist],
    );

    const openLesson = (id: string) => {
        if (String(id) === String(lessonId)) return;
        dispatch(
            navigate({
                routes: APP_ROUTES.SANDBOX_PLAYER.replace(':courseId', courseId).replace(':lessonId', id),
            }),
        );
    };

    const currentIndex = useMemo(() => lessons.findIndex(l => String(l._id) === String(lessonId)), [lessons, lessonId]);
    const moduleTitle = lessons[currentIndex]?.moduleTitle || '';

    const commentCount = useSelector((s: RootState) => (s.project.selectedProjectComments || []).length);

    const pillTabs: { key: TabKey; label: string; count?: number; desktopOnly?: boolean }[] = [
        { key: 'overview', label: 'Overview' },
        { key: 'comments', label: 'Class Comments', count: commentCount },
        { key: 'project', label: 'Project & Tasks' },
        { key: 'assets', label: 'Class Assets', count: classAssetCount || classAssets.length },
        { key: 'classes', label: 'Classes', desktopOnly: true },
    ];

    if (loading) {
        return (
            <Box
                sx={{
                    ...dashboardShellSx,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: CINEMA,
                }}>
                <CircularProgress sx={{ color: OBSIDIAN.primary }} />
            </Box>
        );
    }

    const playlistPanel = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <Box
                sx={{
                    px: 1.5,
                    py: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${OBSIDIAN.outline}33`,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <PlaylistPlayIcon sx={{ color: OBSIDIAN.primary, fontSize: 20 }} />
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            fontWeight: 700,
                            fontSize: 13,
                            color: OBSIDIAN.onSurface,
                        }}>
                        Classes
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: OBSIDIAN.onSurfaceVariant }}>
                        {currentIndex >= 0 ? `${currentIndex + 1}/${lessons.length}` : lessons.length}
                    </Typography>
                </Box>
                {isDesktop && (
                    <Tooltip title="Collapse playlist">
                        <IconButton
                            size="small"
                            onClick={() => setPlaylistOpen(false)}
                            sx={{ color: OBSIDIAN.onSurfaceVariant }}>
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
                <SandboxClassPlaylist
                    lessons={lessons}
                    currentLessonId={lessonId}
                    canWatchAll={canWatchAll}
                    completedLessonIds={completedIds}
                    onSelect={id => openLesson(id)}
                    dense
                />
            </Box>
        </Box>
    );

    return (
        <Box sx={{ ...dashboardShellSx, bgcolor: CINEMA, pb: { xs: 4, md: 4 } }}>
            {/* Top bar */}
            <Box
                sx={{
                    px: { xs: 1.5, md: 2 },
                    height: 56,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    bgcolor: 'rgba(14,14,14,0.85)',
                    backdropFilter: 'blur(16px)',
                    borderBottom: `1px solid ${OBSIDIAN.outline}22`,
                    position: 'sticky',
                    top: 0,
                    zIndex: 20,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                    <IconButton
                        onClick={() =>
                            dispatch(
                                navigate({
                                    routes: APP_ROUTES.SANDBOX_COURSE.replace(':courseId', courseId),
                                }),
                            )
                        }
                        sx={{ color: OBSIDIAN.onSurface, '&:hover': { color: OBSIDIAN.primary } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            fontWeight: 700,
                            fontSize: 13,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: OBSIDIAN.onSurface,
                        }}
                        noWrap>
                        Class Stream Player
                    </Typography>
                </Box>
                {isDesktop && !playlistOpen && (
                    <Tooltip title="Show classes">
                        <IconButton onClick={() => setPlaylistOpen(true)} sx={{ color: OBSIDIAN.primary }}>
                            <PlaylistPlayIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* Lesson + mentor context */}
            <Box sx={{ px: { xs: 2, md: 3 }, pt: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1,
                            py: 0.25,
                            borderRadius: 999,
                            bgcolor: OBSIDIAN.surfaceContainerHigh,
                            color: OBSIDIAN.primary,
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                        }}>
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: OBSIDIAN.secondary,
                                animation: 'pulse 1.6s ease-in-out infinite',
                                '@keyframes pulse': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.35 },
                                },
                            }}
                        />
                        {courseTitle || 'Masterclass'}
                    </Box>
                    {moduleTitle ? (
                        <Typography
                            sx={{ fontFamily: OBSIDIAN_BODY_FONT, fontSize: 12, color: OBSIDIAN.onSurfaceVariant }}>
                            • {moduleTitle}
                        </Typography>
                    ) : null}
                    {masteryTier ? (
                        <Typography
                            sx={{ fontFamily: OBSIDIAN_BODY_FONT, fontSize: 12, color: OBSIDIAN.onSurfaceVariant }}>
                            • {masteryTier}
                        </Typography>
                    ) : null}
                </Box>
                <Typography
                    sx={{
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontWeight: 700,
                        fontSize: { xs: 17, md: 18 },
                        lineHeight: 1.3,
                        color: OBSIDIAN.onSurface,
                    }}>
                    {lesson?.title || 'Lesson'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 0.25 }}>
                    <Avatar
                        src={teacherPhoto || undefined}
                        sx={{ width: 20, height: 20, bgcolor: OBSIDIAN.surfaceContainerHighest, fontSize: 11 }}>
                        {teacherName.slice(0, 1)}
                    </Avatar>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: 12,
                            fontWeight: 600,
                            color: OBSIDIAN.onSurface,
                        }}>
                        {teacherName}
                    </Typography>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.25,
                            px: 0.75,
                            py: 0.1,
                            borderRadius: 999,
                            bgcolor: 'rgba(0,108,80,0.35)',
                            color: OBSIDIAN.secondary,
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: 10,
                            fontWeight: 600,
                        }}>
                        <VerifiedIcon sx={{ fontSize: 12 }} />
                        Mentor
                    </Box>
                </Box>
            </Box>

            {/* Player + desktop playlist */}
            <Box
                sx={{
                    px: { xs: 0, md: 3 },
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'stretch',
                    minHeight: { md: 420 },
                }}>
                <Box sx={{ flex: 1, minWidth: 0, position: 'relative', bgcolor: '#000' }}>
                    {project ? (
                        <SandboxHlsPlayer
                            project={project}
                            startSeconds={startSeconds}
                            fillHeight={isDesktop}
                            onProgress={persist}
                            onCompleted={handleLessonCompleted}
                        />
                    ) : (
                        <Box
                            sx={{
                                p: 4,
                                textAlign: 'center',
                                bgcolor: OBSIDIAN.surfaceContainer,
                                minHeight: 240,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                            <Typography sx={{ color: OBSIDIAN.onSurfaceVariant }}>Lesson media unavailable</Typography>
                        </Box>
                    )}
                    {isDesktop && !playlistOpen && (
                        <Tooltip title="Show classes">
                            <IconButton
                                onClick={() => setPlaylistOpen(true)}
                                sx={{
                                    display: { xs: 'none', md: 'flex' },
                                    position: 'absolute',
                                    right: 8,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: OBSIDIAN.surfaceContainerHigh,
                                    color: OBSIDIAN.primary,
                                    border: `1px solid ${OBSIDIAN.outline}44`,
                                    '&:hover': { bgcolor: OBSIDIAN.surfaceContainerHighest },
                                    zIndex: 2,
                                }}>
                                <ChevronLeftIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                {isDesktop && playlistOpen && (
                    <Box
                        sx={{
                            width: PLAYLIST_WIDTH,
                            flexShrink: 0,
                            bgcolor: OBSIDIAN.surfaceContainer,
                            borderRadius: 1,
                            border: `1px solid ${OBSIDIAN.outline}33`,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            maxHeight: { md: 'min(70vh, 640px)' },
                        }}>
                        {playlistPanel}
                    </Box>
                )}
            </Box>

            {/* Pill tabs */}
            <Box
                sx={{
                    position: 'sticky',
                    top: 56,
                    zIndex: 15,
                    mt: 1.5,
                    px: { xs: 2, md: 3 },
                    py: 1.25,
                    bgcolor: 'rgba(14,14,14,0.95)',
                    backdropFilter: 'blur(16px)',
                    borderBottom: `1px solid ${OBSIDIAN.outline}22`,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflowX: 'auto', pb: 0.25 }}>
                    {pillTabs
                        .filter(t => !(t.desktopOnly && isDesktop))
                        .map(t => {
                            const active = tab === t.key;
                            return (
                                <Box
                                    key={t.key}
                                    component="button"
                                    type="button"
                                    onClick={() => setTab(t.key)}
                                    sx={{
                                        border: 0,
                                        cursor: 'pointer',
                                        px: 1.75,
                                        py: 0.75,
                                        borderRadius: 999,
                                        whiteSpace: 'nowrap',
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                        fontSize: 12,
                                        fontWeight: active ? 700 : 500,
                                        bgcolor: active ? OBSIDIAN.primary : OBSIDIAN.surfaceContainer,
                                        color: active ? OBSIDIAN.onPrimaryFixed : OBSIDIAN.onSurfaceVariant,
                                        boxShadow: active ? '0 1px 4px rgba(0,0,0,0.25)' : 'none',
                                        transition: 'all 0.15s',
                                        '&:hover': {
                                            color: active ? OBSIDIAN.onPrimaryFixed : OBSIDIAN.onSurface,
                                        },
                                    }}>
                                    {t.label}
                                    {typeof t.count === 'number' && t.count > 0 ? (
                                        <Box
                                            component="span"
                                            sx={{
                                                ml: 0.75,
                                                px: 0.75,
                                                py: 0.1,
                                                borderRadius: 999,
                                                bgcolor: active
                                                    ? OBSIDIAN.onPrimaryFixed
                                                    : OBSIDIAN.surfaceContainerHighest,
                                                color: active ? OBSIDIAN.primary : OBSIDIAN.onSurfaceVariant,
                                                fontSize: 10,
                                                fontWeight: 700,
                                            }}>
                                            {t.count}
                                        </Box>
                                    ) : null}
                                </Box>
                            );
                        })}
                </Box>
            </Box>

            <Box sx={{ px: { xs: 2, md: 3 }, pt: 2, pb: 4 }}>
                {tab === 'overview' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 720 }}>
                        {courseDescription ? <SandboxRichText content={courseDescription} /> : null}
                        {lesson?.description && lesson.description !== courseDescription ? (
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        letterSpacing: '0.08em',
                                        textTransform: 'uppercase',
                                        color: OBSIDIAN.primary,
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                        mb: 1,
                                    }}>
                                    This class
                                </Typography>
                                <SandboxRichText content={lesson.description} />
                            </Box>
                        ) : null}
                        {!courseDescription && !lesson?.description ? (
                            <SandboxRichText fallback="Watch the lesson and complete any attached project." />
                        ) : null}
                        {lessons.length > 0 ? (
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        letterSpacing: '0.08em',
                                        textTransform: 'uppercase',
                                        color: OBSIDIAN.primary,
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                        mb: 1,
                                    }}>
                                    Course breakdown
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {lessons.map((item, index) => (
                                        <Box
                                            key={item._id}
                                            sx={{
                                                p: 1.25,
                                                borderRadius: 2,
                                                bgcolor: OBSIDIAN.surfaceContainer,
                                                border: `1px solid ${
                                                    String(item._id) === String(lessonId)
                                                        ? `${OBSIDIAN.primary}55`
                                                        : `${OBSIDIAN.outline}22`
                                                }`,
                                            }}>
                                            <Typography
                                                sx={{
                                                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                                                    fontWeight: 700,
                                                    fontSize: 13,
                                                    color: OBSIDIAN.onSurface,
                                                }}>
                                                Class {index + 1}: {item.title}
                                            </Typography>
                                            {item.description ? (
                                                <Typography
                                                    sx={{
                                                        mt: 0.35,
                                                        fontFamily: OBSIDIAN_BODY_FONT,
                                                        fontSize: 12,
                                                        color: OBSIDIAN.onSurfaceVariant,
                                                        lineHeight: 1.5,
                                                    }}>
                                                    {item.description}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        ) : null}
                    </Box>
                )}

                {tab === 'comments' && project ? (
                    <SandboxLessonDiscussion
                        project={project}
                        assignmentBrief={lesson?.has_assignment ? lesson?.assignment_brief : undefined}
                        onGoToProject={() => setTab('project')}
                    />
                ) : null}
                {tab === 'comments' && !project ? (
                    <Typography sx={{ color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                        Discussion unlocks when class media is available.
                    </Typography>
                ) : null}

                {tab === 'project' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {lesson?.has_assignment ? (
                            <SandboxAssignmentSubmitPanel
                                courseId={courseId}
                                lessonId={lessonId}
                                brief={lesson.assignment_brief}
                            />
                        ) : (
                            <Typography sx={{ color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                                No assignment on this lesson.
                            </Typography>
                        )}
                    </Box>
                )}

                {tab === 'assets' && (
                    <SandboxClassAssetsPanel courseId={courseId} canAccess={canWatchAll} isSignedIn={isSignedIn} />
                )}

                {tab === 'classes' && !isDesktop && (
                    <Box
                        sx={{
                            bgcolor: OBSIDIAN.surfaceContainer,
                            borderRadius: 1,
                            border: `1px solid ${OBSIDIAN.outline}33`,
                            overflow: 'hidden',
                        }}>
                        {playlistPanel}
                    </Box>
                )}
            </Box>
        </Box>
    );
}
